import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';

export interface ToastData {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number; // en milisegundos, 0 para persistente
  actions?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  }[];
}

interface ToastContextType {
  toasts: ToastData[];
  addToast: (toast: Omit<ToastData, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
  // Métodos de conveniencia
  success: (title: string, message?: string, duration?: number) => string;
  error: (title: string, message?: string, duration?: number) => string;
  warning: (title: string, message?: string, duration?: number) => string;
  info: (title: string, message?: string, duration?: number) => string;
  // Método para confirmaciones
  confirm: (
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void
  ) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast debe usarse dentro de ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((toast: Omit<ToastData, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: ToastData = {
      ...toast,
      id,
      duration: toast.duration ?? 5000, // 5 segundos por defecto
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto-remove después del duration especificado
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Métodos de conveniencia
  const success = useCallback(
    (title: string, message?: string, duration?: number) => {
      return addToast({ type: 'success', title, message, duration });
    },
    [addToast]
  );

  const error = useCallback(
    (title: string, message?: string, duration?: number) => {
      return addToast({ type: 'error', title, message, duration });
    },
    [addToast]
  );

  const warning = useCallback(
    (title: string, message?: string, duration?: number) => {
      return addToast({ type: 'warning', title, message, duration });
    },
    [addToast]
  );

  const info = useCallback(
    (title: string, message?: string, duration?: number) => {
      return addToast({ type: 'info', title, message, duration });
    },
    [addToast]
  );

  // Método para confirmaciones
  const confirm = useCallback(
    (
      title: string,
      message: string,
      onConfirm: () => void,
      onCancel?: () => void
    ) => {
      return addToast({
        type: 'warning',
        title,
        message,
        duration: 0, // Persistente hasta que el usuario actúe
        actions: [
          {
            label: 'Confirmar',
            onClick: () => {
              onConfirm();
              // El toast se cierra automáticamente cuando se hace click en una acción
            },
            variant: 'primary',
          },
          {
            label: 'Cancelar',
            onClick: () => {
              if (onCancel) onCancel();
              // El toast se cierra automáticamente cuando se hace click en una acción
            },
            variant: 'secondary',
          },
        ],
      });
    },
    [addToast]
  );

  const value: ToastContextType = {
    toasts,
    addToast,
    removeToast,
    clearAll,
    success,
    error,
    warning,
    info,
    confirm,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

// Componente para renderizar los toasts
const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3 max-w-sm w-full">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

interface ToastProps {
  toast: ToastData;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return (
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
        );
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />;
      case 'warning':
        return (
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        );
      case 'info':
        return <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
      default:
        return <Info className="h-5 w-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'bg-white dark:bg-gray-800',
          border: 'border border-green-200 dark:border-green-700',
          accent: 'border-l-4 border-l-green-500',
        };
      case 'error':
        return {
          bg: 'bg-white dark:bg-gray-800',
          border: 'border border-red-200 dark:border-red-700',
          accent: 'border-l-4 border-l-red-500',
        };
      case 'warning':
        return {
          bg: 'bg-white dark:bg-gray-800',
          border: 'border border-amber-200 dark:border-amber-700',
          accent: 'border-l-4 border-l-amber-500',
        };
      case 'info':
        return {
          bg: 'bg-white dark:bg-gray-800',
          border: 'border border-blue-200 dark:border-blue-700',
          accent: 'border-l-4 border-l-blue-500',
        };
      default:
        return {
          bg: 'bg-white dark:bg-gray-800',
          border: 'border border-gray-200 dark:border-gray-700',
          accent: 'border-l-4 border-l-gray-500',
        };
    }
  };

  const styles = getStyles();

  return (
    <div
      className={`
        ${styles.bg} ${styles.border} ${styles.accent}
        rounded-lg shadow-lg p-4 
        transform transition-all duration-300 ease-in-out
        animate-in slide-in-from-right-5
        opacity-100
      `}
    >
      <div className="flex items-start space-x-3">
        {/* Icono */}
        <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>

        {/* Contenido */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                {toast.title}
              </h4>
              {toast.message && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {toast.message}
                </p>
              )}
            </div>

            {/* Botón de cerrar */}
            <button
              onClick={onClose}
              className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Acciones */}
          {toast.actions && toast.actions.length > 0 && (
            <div className="mt-3 flex space-x-2">
              {toast.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => {
                    action.onClick();
                    onClose(); // Cerrar el toast después de la acción
                  }}
                  className={`
                    px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                    ${
                      action.variant === 'primary'
                        ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                    }
                  `}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToastProvider;
